import React, { useState, useCallback } from 'react';
import { X, Search, Loader2 } from 'lucide-react';

const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || 'sXpGFDGZs0Dv1mmNFvYaGUvYwKX0PWIh';

interface GifResult {
  id: string;
  url: string;
  title: string;
  width: number;
  height: number;
}

interface GifPickerProps {
  onSelect: (gifUrl: string) => void;
  onClose: () => void;
  isOpen: boolean;
  className?: string;
}

export function GifPicker({ onSelect, onClose, isOpen, className = '' }: GifPickerProps) {
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<GifResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const searchGifs = useCallback(async (searchQuery: string) => {
    setLoading(true);
    setSearched(!!searchQuery.trim());
    try {
      const url = searchQuery.trim()
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(searchQuery)}&limit=20&rating=g`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=g`;

      const res = await fetch(url);
      const data = await res.json();

      if (data.data) {
        setGifs(
          data.data.map((g: any) => ({
            id: g.id,
            url: g.images?.downsized_medium?.url || g.images?.fixed_height?.url || g.images?.original?.url,
            title: g.title || '',
            width: g.images?.downsized_medium?.width || 200,
            height: g.images?.downsized_medium?.height || 200,
          }))
        );
      } else {
        setGifs([]);
      }
    } catch (err) {
      console.error('Giphy search error:', err);
      setGifs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchGifs(query);
  };

  const handleSelect = (gif: GifResult) => {
    onSelect(gif.url);
    onClose();
  };

  // Load trending on open
  React.useEffect(() => {
    if (isOpen && gifs.length === 0 && !searched) {
      searchGifs('');
    }
  }, [isOpen, searchGifs, gifs.length, searched]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center sm:items-center justify-center p-2 sm:p-4 bg-black/60 ${className}`}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full sm:max-w-lg max-h-[50vh] sm:max-h-[60vh] flex flex-col overflow-hidden m-2 sm:m-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h3 className="font-semibold text-content text-sm sm:text-base">Choose a GIF</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSearch} className="p-3 sm:p-4 border-b border-gray-100">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search GIFs..."
                className="w-full pl-9 pr-3 py-2 sm:py-2 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-3 sm:px-4 py-2 bg-accent-text text-white rounded-lg hover:bg-accent-text/90 disabled:opacity-50 text-sm font-medium touch-manipulation"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </button>
          </div>
        </form>

        <div className="flex-1 overflow-y-auto p-2 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent-text" />
            </div>
          ) : gifs.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-500 text-sm">
              {searched ? 'No GIFs found. Try a different search.' : 'Search for a GIF above.'}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-1 sm:gap-2">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  type="button"
                  onClick={() => handleSelect(gif)}
                  className="aspect-square rounded-lg overflow-hidden hover:ring-2 ring-accent-text transition-all focus:outline-none focus:ring-2 focus:ring-accent-text touch-manipulation"
                >
                  <img
                    src={gif.url}
                    alt={gif.title || 'GIF'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
